import { Injectable } from '@angular/core';
import {
  ActionSheetController,
  LoadingController,
  AlertController,
} from '@ionic/angular';
import { HeImage, File, Permissions } from '@app/core/models';
import { ImageService } from '@app/core/async-services/http/versioned/image.service';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import {
  FileService,
  PlatformError,
  InvalidFileError,
} from '../file/file.service';
import { ClearStateService } from '../clear-state/clear-state.service';
import { map } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { PermissionService } from '../permissions/permission.service';
import { ActionSheetButton } from '@ionic/core';
import { firstTruthy } from '@app/core/utils/rxjs.util';

const isPermissionError = (error: any): boolean => error.code === 3;
const isUserCanceledError = (error: any): boolean =>
  typeof error === 'string' && error.includes('canceled');

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private _image$ = new BehaviorSubject<HeImage | null>(null);
  private _file$ = new BehaviorSubject<File.File | null>(null);
  private _fileUrl$ = new BehaviorSubject<string | null>(null);

  constructor(
    private _actionSheetController: ActionSheetController,
    private _loadingController: LoadingController,
    private _imageService: ImageService,
    private _fileService: FileService,
    private _clearStateService: ClearStateService,
    private _logger: NGXLogger,
    private _alertController: AlertController,
    private _permissionService: PermissionService,
  ) {
    this._clearStateService.clearState$.subscribe(() =>
      this.clearAttachments(),
    );
  }

  get image$(): Observable<HeImage | null> {
    return this._image$.asObservable();
  }

  get file$(): Observable<File.File | null> {
    return this._file$.asObservable();
  }

  get image(): HeImage | null {
    return this._image$.value;
  }

  get file(): File.File | null {
    return this._file$.value;
  }

  get hasImage(): boolean {
    return this.image !== null;
  }

  get hasFile(): boolean {
    return this.file !== null;
  }

  async addAttachment(): Promise<void> {
    const actionSheet = await this._createActionSheet();
    actionSheet.present();
  }

  onFileUpload(): Promise<{ url: string }> {
    return firstValueFrom(
      this._fileUrl$.pipe(
        firstTruthy(),
        map((url) => ({ url })),
      ),
    );
  }

  clearImage(): void {
    this._image$.next(null);
  }

  clearFile(): void {
    this._file$.next(null);
    this._fileUrl$.next(null);
  }

  clearAttachments(): void {
    this.clearImage();
    this.clearFile();
  }

  private _createActionSheet(): Promise<HTMLIonActionSheetElement> {
    const buttons: ActionSheetButton[] = [
      {
        text: `Add Image`,
        icon: 'fal-image',
        cssClass: this.hasImage ? 'disabled-action-sheet-button' : undefined,
        handler: this.hasImage ? () => false : () => this._addImage(),
      },
      {
        text: 'Cancel',
        role: 'cancel',
      },
    ];
    const canUserSendFile = this._canUserSendFile();
    if (canUserSendFile) {
      buttons.splice(1, 0, {
        text: 'Add File',
        icon: 'fal-file',
        cssClass: this.hasFile ? 'disabled-action-sheet-button' : undefined,
        handler: this.hasFile ? () => false : () => this._addFile(),
      });
    }
    const header = canUserSendFile
      ? 'You may attach 1 image and 1 file'
      : 'You may attach 1 image';
    return this._actionSheetController.create({ header, buttons });
  }

  private _canUserSendFile(): boolean {
    return this._permissionService.doesUserHavePermission(
      Permissions.Permission.CanSendFileInAnnouncement,
    );
  }

  async _addImage(): Promise<boolean> {
    const loadingOverlay = await this._loadingController.create();
    await loadingOverlay.present();
    try {
      const image = await this._imageService.newImage();
      this._image$.next(image);
      return true;
    } catch (error) {
      this._logger.error(
        'Unexpected error occurred when adding an image as an attachment.',
        error,
      );
      return false;
    } finally {
      loadingOverlay.dismiss();
    }
  }

  async _addFile(): Promise<boolean> {
    const loadingOverlay = await this._loadingController.create();
    await loadingOverlay.present();
    try {
      const file = await this._fileService.pick();
      this._setFile(file);
      this._uploadFile(file);
      return true;
    } catch (error) {
      this._handleAddFileError(error);
      return false;
    } finally {
      loadingOverlay.dismiss();
    }
  }

  private _setFile({ fileName, blob }: File.Raw): void {
    this._file$.next({
      name: fileName,
      type: File.Type.DOCUMENT,
      size: blob.size,
      contentType: blob.type,
    });
  }

  private _uploadFile(file: File.Raw): void {
    this._fileService
      .upload(file)
      .then(({ url }) => this._fileUrl$.next(url))
      .catch(() => this._file$.next(null));
  }

  private async _handleAddFileError(
    error: PlatformError | InvalidFileError | unknown,
  ): Promise<void> {
    if (error instanceof PlatformError || error instanceof InvalidFileError) {
      const alert = await this._alertController.create({
        message: error.message,
        buttons: ['OK'],
      });
      alert.present();
    } else if (isPermissionError(error)) {
      const alert = await this._alertController.create({
        header: 'Permission Denied',
        message: 'Please update app permissions in the device settings.',
        buttons: ['OK'],
      });
      alert.present();
    } else if (!isUserCanceledError(error)) {
      this._logger.error(
        'Unexpected error occurred when adding a file as an attachment.',
        error,
      );
    }
  }
}
